import type { Updater } from '@tanstack/vue-table'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { customAlphabet } from 'nanoid'

const shownToasts = new Set<string>()

type ToastType = 'success' | 'error' | 'loading' | 'custom'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function valueUpdater<T extends Updater<unknown>>(
  updaterOrValue: T,
  ref: Ref,
) {
  ref.value =
    typeof updaterOrValue === 'function'
      ? updaterOrValue(ref.value)
      : updaterOrValue
}

export function focusOnError() {
  const errorInput = document.querySelector(
    '[aria-invalid="true"]',
  ) as HTMLElement
  if (errorInput) {
    errorInput.scrollIntoView(false)
    errorInput.focus()
  }
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function retryFn(
  fn: () => Promise<unknown>,
  options: { maxRetries?: number; retryDelay?: number } = {},
) {
  const { maxRetries = 10, retryDelay = 2000 } = options
  const attempt = async (retries: number) => {
    try {
      return await fn()
    } catch (e) {
      if (retries > 0) {
        await sleep(retryDelay)
        return attempt(retries - 1)
      } else {
        throw e
      }
    }
  }
  return attempt(maxRetries)
}

export function loadScript({ src, id }: { src: string; id: string }) {
  return new Promise<{ src: string; id: string }>((resolve) => {
    const d = document
    const s = 'script'
    if (d.getElementById(id)) {
      resolve({ src, id })
      return
    }
    const js = d.createElement(s)
    js.id = id
    js.src = src
    js.addEventListener(
      'load',
      () => {
        resolve({ src, id })
      },
      { once: true },
    )
    d.body.appendChild(js)
  })
}

export const getDeviceUuid = customAlphabet(
  '0123456789abcdefghijklmnopqrstuvwxyz-',
  36,
)

export function showToastOnce(
  message: string,
  code: number | string,
  type: ToastType = 'error',
  ttl = 5000,
) {
  const key = `toast-code-${type}-${code}`
  if (shownToasts.has(key)) return
  shownToasts.add(key)
  switch (type) {
    case 'success':
      toast.success(message)
      break
    case 'loading':
      toast.loading(message)
      break
    case 'custom':
      toast(message, {
        style: {
          border: '1px solid #333',
          padding: '16px',
          color: '#333',
        },
        icon: '✨',
      })
      break
    default:
      toast.error(message)
  }

  setTimeout(() => shownToasts.delete(key), ttl)
}
